import { Link } from "react-router-dom";
import { ArrowRight, FileText, Download } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Seo } from "../components/site/Seo";
import { CTASection } from "../components/site/CTASection";
import { REPORTS } from "../data/reports";

const SITE = "https://matrivon.com";
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { month: "short", year: "numeric" });

export default function MarketIntelligence() {
  const list = [...REPORTS].sort((a, b) => new Date(b.published) - new Date(a.published));
  const groups = [
    { name: "Polyurethane", items: list.filter((r) => !r.slug.includes("silicone")) },
    { name: "Silicone", items: list.filter((r) => r.slug.includes("silicone")) },
  ].filter((g) => g.items.length);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "MATRIVON Market Intelligence Reports",
    description:
      "Independent polyurethane and specialty-chemical market intelligence reports covering the GCC, Middle East and wider region.",
    url: `${SITE}/market-intelligence`,
    publisher: { "@type": "Organization", name: "MATRIVON", url: SITE },
  };

  return (
    <div>
      <Seo
        title="Market Intelligence Reports | Polyurethane & Chemicals | MATRIVON"
        description="Independent MATRIVON market intelligence reports — polyurethane demand, raw materials, system houses and commercial opportunities across the GCC, Middle East and beyond."
        path="/market-intelligence"
        jsonLd={jsonLd}
      />
      <PageHero
        variant="dark"
        label="Market Intelligence"
        title="Independent Market Intelligence Reports"
        intro="Data-driven, technically grounded assessments of polyurethane and specialty-chemical markets — demand, raw materials, competitive structure and commercial opportunity. Published regularly for industry decision-makers."
        crumbs={[{ label: "Market Intelligence" }]}
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 pt-12 lg:px-8">
          <Link
            to="/market-intelligence/global-silicone-industry-2026"
            data-testid="featured-global-silicone-industry"
            className="group grid overflow-hidden border border-brand-line bg-white transition-colors hover:border-brand-blue/50 lg:grid-cols-[1.3fr_1fr]"
          >
            <div className="aspect-[16/9] w-full overflow-hidden bg-brand-bg lg:aspect-auto">
              <img src="/global-silicone-industry-value-chain-2026.webp" alt="Global silicone industry value chain from silicon metal to specialty silicone applications" width={1200} height={630} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-10">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-red">Featured · Silicone Industry</span>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-brand-ink group-hover:text-brand-blue lg:text-3xl">Beyond the "Top Silicone Companies" Lists: How the Global Silicone Industry Is Really Structured in 2026</h2>
              <p className="mt-4 text-sm leading-relaxed text-brand-muted">Integrated producers, China's capacity, specialty formulators and the opportunities in Türkiye and the GCC — a practical map beyond logo rankings.</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">Read the analysis <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </div>
          </Link>
        </div>
      </section>

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl space-y-14 px-5 py-16 lg:px-8">
          {groups.map((group) => (
            <div key={group.name}>
              <h2 className="mb-5 font-heading text-2xl font-bold text-brand-ink" data-testid={`report-group-${group.name.toLowerCase()}`}>
                {group.name} <span className="text-brand-muted">· {group.items.length}</span>
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {group.items.map((r) => (
                  <Link
                    key={r.slug}
                    to={r.path}
                    data-testid={`report-card-${r.slug}`}
                    className="group flex flex-col overflow-hidden border border-brand-line bg-white transition-colors hover:border-brand-blue/40"
                  >
                    <div className="aspect-[16/9] w-full overflow-hidden bg-brand-bg">
                      <img
                        src={r.image}
                        alt={r.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-red">
                        <FileText className="h-4 w-4" /> {r.category} · {r.period}
                      </div>
                      <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-brand-ink group-hover:text-brand-blue lg:text-2xl">
                        {r.title}
                      </h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-muted">{r.excerpt}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {r.headlineStats.slice(0, 2).map((s) => (
                          <span key={s.label} className="border border-brand-line bg-brand-bg px-3 py-1.5 text-xs text-brand-ink">
                            {s.value} <span className="text-brand-muted">· {s.label}</span>
                          </span>
                        ))}
                      </div>
                      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue">
                        <Download className="h-4 w-4" /> Read & download report
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                      <span className="mt-3 text-xs text-brand-muted">Published {fmtDate(r.published)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        label="Custom intelligence"
        title="Need a Report Built Around Your Market?"
        body="MATRIVON prepares bespoke market and competitor intelligence tailored to your products, regions and commercial questions."
        primary={{ to: "/contact", label: "Discuss a Custom Market Intelligence Project" }}
        secondary={{ to: "/polyurethane-consulting", label: "Polyurethane Consulting" }}
      />
    </div>
  );
}
